import React from 'react';
import { styled } from '@mui/material/styles';

interface MultipleSelectionCheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
  'aria-label'?: string;
  variant?: 'selectAll' | 'card';
}

const CheckboxContainer = styled('div')<{
  disabled: boolean;
  checked: boolean;
  indeterminate: boolean;
  variant: 'selectAll' | 'card';
}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  box-sizing: border-box;
  transition: all 0.2s ease;
  position: relative;

  /* Remove default browser focus outline */
  outline: none;

  /* Circular button-like focus styling to match other card buttons */
  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);

    & rect.checkbox-border {
      ${(props) => {
        if (props.disabled) return '';

        if (props.variant === 'selectAll') {
          return `stroke: #FFFFFF;`; // Always white for select-all
        }

        if (props.variant === 'card') {
          if (props.checked || props.indeterminate) {
            return `stroke: #6DDAE2;`; // Teal border when checked
          } else {
            return `stroke: #FFFFFF;`; // White border when unchecked
          }
        }

        return '';
      }}
    }
  }
`;

const CheckIcon = styled('svg')<{ variant: 'selectAll' | 'card'; checked: boolean; indeterminate: boolean }>`
  width: 12px;
  height: 12px;
  position: relative;
  color: ${(props) => {
    if (props.variant === 'card') {
      return props.checked || props.indeterminate ? '#6DDAE2' : '#FFFFFF';
    }
    return '#FFFFFF'; // selectAll variant
  }};
  fill: ${(props) => {
    if (props.variant === 'card') {
      return props.checked || props.indeterminate ? '#6DDAE2' : '#FFFFFF';
    }
    return '#FFFFFF'; // selectAll variant
  }};
  shape-rendering: geometricPrecision;
`;

const MultipleSelectionCheckbox: React.FC<MultipleSelectionCheckboxProps> = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  className,
  'aria-label': ariaLabel,
  variant = 'selectAll',
}) => {
  const handleClick = (event: React.MouseEvent) => {
    if (!disabled && onChange) {
      // Only prevent event bubbling when we have an onChange handler
      // This allows select all checkbox to work with parent div's onClick
      event.stopPropagation();
      onChange(!checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Support Space and Enter keys for keyboard accessibility
    if (!disabled && onChange && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      event.stopPropagation();
      onChange(!checked);
    }
  };

  const getBorderColor = () => {
    if (variant === 'card') {
      return checked || indeterminate ? '#6DDAE2' : '#FFFFFF';
    }
    return '#FFFFFF'; // selectAll variant
  };

  return (
    <CheckboxContainer
      className={className}
      disabled={disabled}
      checked={checked}
      indeterminate={indeterminate}
      variant={variant}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
    >
      <CheckIcon
        viewBox="0 0 12 12"
        variant={variant}
        checked={checked}
        indeterminate={indeterminate}
        preserveAspectRatio="xMidYMid meet"
      >
        <rect
          className="checkbox-border"
          x="0.75"
          y="0.75"
          width="10.5"
          height="10.5"
          rx="1"
          fill="none"
          stroke={getBorderColor()}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
        {indeterminate && <rect x="3" y="5.5" width="6" height="1" fill="#FFFFFF" vectorEffect="non-scaling-stroke" />}
        {checked && !indeterminate && (
          <path
            d="M8.5 4L5 7L3.5 5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </CheckIcon>
    </CheckboxContainer>
  );
};

export default MultipleSelectionCheckbox;
